import { History, Location } from 'history'
import { parse, ParsedQuery, stringify } from 'query-string'

import { SEARCH_URL_STORAGE_KEY } from '@at/window-utils/lib/local-storage-utils/local-storage-keys'

import { setItemLocalStorage } from '@at/window-utils/lib/local-storage-utils/local-storage.utils'

import { FilterInput, FilterName } from '../__generated__/fed-graphql-types'

import { OptionValue, setFilterSelectedOptions } from './search/search-results-util/search-results.util'

import {
    getChannelFromPathname,
    getChannelFromPathnameOrAdvertisingLocation,
    searchUrlFromAdvertisingLocation,
} from './channel-utilities'

import {
    getFilterAndValueFromQueryParam,
    getFilterToQueryParamOverrides,
    getQueryParamFromFacetValue,
    getQueryParamToFiltersOverrides,
    removeDeprecatedDuplicateParams,
    removeOtherSearchFilterValueQueryParams,
} from './search/filter-query-param-util/filter-query-param-util'
import { purgeCache } from '../graphql/cache'
import { FilterQueryParam } from './search/filter-query-param-util/query-params'
import { Channel } from '@at/sauron-platform-core'
import { DeepWriteable } from './deep-writeable'
import { SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet } from '../contexts/search-results-context/query/__generated__/search-results-facets.fed.query'

const FILTERS_USING_COMMA_SEPARATOR = [
    FilterName.body_type,
    FilterName.colour,
    FilterName.fuel_type,
    FilterName.keywords,
]

export const getPageNumberQueryParam = (location: Location) => {
    let { page } = parse(location.search, {
        parseNumbers: true,
    })
    page = Math.abs(Number(page))
    if (!page) return 1

    return page
}

const removeQueryParametersForBackLink = (location: Location) => {
    const queryParameters = parse(location.search)
    queryParameters.leadType = null
    queryParameters.clickAndCollectDealerName = null
    return stringify(queryParameters)
}

export const getBackToSearchUrl = (location: Location): string => {
    const advertisingLocation =
        location.search && (parse(location.search.replace('?', ''))['advertising-location'] || '')
    const searchUrl = searchUrlFromAdvertisingLocation(advertisingLocation as string)
    return `${searchUrl}?${removeQueryParametersForBackLink(location)}`
}

export const getEncodedSearchUrl = (location: Location): string => {
    return encodeURIComponent(`/car-search${location.search}`)
}

type SearchFiltersWithValues = {
    [key: string]: OptionValue[] | [undefined]
}

const getFiltersWithValues = (
    filter: FilterName,
    parsedSearch: ParsedQuery,
    selected: OptionValue[] | [undefined] = [undefined],
    channel: Channel
): SearchFiltersWithValues | object => {
    const filterQueryParamOverride = getFilterToQueryParamOverrides(channel)[filter]

    if (typeof filterQueryParamOverride === 'object') {
        removeOtherSearchFilterValueQueryParams(filterQueryParamOverride, parsedSearch)
        return getQueryParamFromFacetValue(filterQueryParamOverride, selected)
    }

    const searchFiltersWithValues: SearchFiltersWithValues = {}

    const filterQueryParamKey = filterQueryParamOverride || filter

    searchFiltersWithValues[filterQueryParamKey] = selected.filter((value) => value !== '')

    return searchFiltersWithValues
}

export interface FilterWithSelectedOptions {
    filter: FilterName
    selected?: OptionValue[]
}

interface SetSelectedFilterOption {
    (history: History, filtersWithSelectedOptions: FilterWithSelectedOptions[], sort: string | null): void
}

export const updateUrl = (
    history: History,
    params: ParsedQuery<string>,
    currentParamsMinusDeletedParams: ParsedQuery<string>,
    pushUpdate = true
) => {
    purgeCache()

    const { pathname, search, hash = '' } = history.location
    const currentQueryParams = parse(search)

    if (currentQueryParams.page != undefined && params.page === undefined) {
        currentParamsMinusDeletedParams.page = null
    }

    if (currentQueryParams['expired-ad'] != undefined) {
        currentParamsMinusDeletedParams['expired-ad'] = null
    }

    const queryParamsObject = { ...currentParamsMinusDeletedParams, ...params }
    const queryWithDeprecatedParamsRemoved = removeDeprecatedDuplicateParams(queryParamsObject)
    const queryParams: string = stringify(queryWithDeprecatedParamsRemoved, { skipNull: true })

    const queryString = queryParams ? `?${queryParams}` : ''
    const updatedUrl = pathname + queryString + hash

    if (pushUpdate) {
        setItemLocalStorage(SEARCH_URL_STORAGE_KEY, updatedUrl)
        history.push(updatedUrl, queryWithDeprecatedParamsRemoved)
    }
}

// TODO: This function combined with updateUrl is quite confusing. Suggest a refactor
export const updateUrlFromSelectedFilters: SetSelectedFilterOption = (history, filtersWithSelectedOptions, sort) => {
    const { search, pathname } = history.location
    const parsedSearch = parse(search)

    let searchFiltersWithValues = {}

    filtersWithSelectedOptions.forEach(({ filter, selected }) => {
        const selectedFilters = getFiltersWithValues(filter, parsedSearch, selected, getChannelFromPathname(pathname))
        searchFiltersWithValues = { ...searchFiltersWithValues, ...selectedFilters }
    })

    if ((parsedSearch.sort ?? null) !== sort) {
        searchFiltersWithValues = { ...searchFiltersWithValues, sort }
    }

    updateUrl(history, searchFiltersWithValues, parsedSearch)
}

export const camelCaseToUnderscores = (word: string) =>
    word.split(/(?=[A-Z])/).reduce((words, word) => {
        if (!words) return word
        return `${words}_${word.toLowerCase()}`
    }, '')

const kebabCaseToUnderscores = (word: string) => word.replace(/-/g, '_').toLowerCase()

export const getFilterNameAndValue = (queryParamKey: string, queryParamValue: string | string[], channel: Channel) => {
    const filterQueryParamOverrideEntries = Object.entries(getQueryParamToFiltersOverrides(channel))

    const filterQueryParamNameOverride = filterQueryParamOverrideEntries.find(
        ([, queryParamOverride]) => queryParamOverride === queryParamKey
    )

    if (filterQueryParamNameOverride) {
        const [filterName] = filterQueryParamNameOverride
        return {
            filterName: FilterName[filterName as FilterName],
            value: queryParamValue,
        }
    }

    const filterQueryParamNameAndValueOverride = getFilterAndValueFromQueryParam(
        queryParamKey,
        queryParamValue,
        channel
    )
    if (filterQueryParamNameAndValueOverride?.length > 0) {
        const [filterName, value] = filterQueryParamNameAndValueOverride
        return { filterName: FilterName[filterName as FilterName], value }
    }

    const filterName = FilterName[kebabCaseToUnderscores(camelCaseToUnderscores(queryParamKey)) as FilterName]
    if (!!filterName) {
        return { filterName: filterName, value: queryParamValue }
    }

    return { filterName: undefined, value: undefined }
}

const hasAnyMonthlyPriceSearchParams = (queryParams: ParsedQuery) => {
    return (
        queryParams[FilterQueryParam[FilterName.term]] ||
        queryParams[FilterQueryParam[FilterName.deposit]] ||
        queryParams[FilterQueryParam[FilterName.min_monthly_price]] ||
        queryParams[FilterQueryParam[FilterName.max_monthly_price]] ||
        queryParams[FilterQueryParam[FilterName.yearly_mileage]]
    )
}

const isNationalDistance = (filterName: FilterName, optionValue: (string | undefined)[]): boolean => {
    return filterName === FilterName.distance && (optionValue.includes('1500') || optionValue.includes('1501'))
}

const isLocationFilter = (filterName: FilterName) =>
    [FilterName.postcode, FilterName.lat_long, FilterName.location].includes(filterName)

export const makeFiltersFromUrl = (location: Location): FilterInput[] => {
    if (!location) {
        return []
    }

    const { search, state, pathname } = location
    const queryParams = parse(search)
    const isLeasingSearch = /(car|van)-leasing-search/.test(location.pathname)
    const channel = getChannelFromPathnameOrAdvertisingLocation(pathname, search)

    const filters: FilterInput[] = []
    if (isLeasingSearch) {
        filters.push({
            filter: FilterName.price_search_type,
            selected: ['leasing'],
        })
        if (queryParams[FilterQueryParam[FilterName.lease_product_type]] === undefined) {
            if (channel === Channel.vans) {
                filters.push({
                    filter: FilterName.lease_product_type,
                    selected: ['business'],
                })
            } else {
                filters.push({
                    filter: FilterName.lease_product_type,
                    selected: ['personal'],
                })
            }
        }
    } else if (!queryParams.price_search_type) {
        filters.push({
            filter: FilterName.price_search_type,
            selected: [hasAnyMonthlyPriceSearchParams(queryParams) ? 'monthly' : 'total'],
        })
    }

    Object.entries(state || queryParams).forEach(([queryParamKey, queryParamValue]) => {
        const { filterName, value } = getFilterNameAndValue(
            queryParamKey,
            queryParamValue as string[],
            getChannelFromPathnameOrAdvertisingLocation(pathname, search)
        )
        if (filterName) {
            let values: (string | undefined)[] = Array.isArray(value) ? value : [value]
            if (FILTERS_USING_COMMA_SEPARATOR.includes(filterName)) {
                values = values.flatMap((value) => value?.split(',').map((term) => term.trim()))
            }

            let optionValue = values.filter((selectedValue) => !!selectedValue)
            //TODO tidy up advertising_location issue: short hack to clean advertising_location values
            if (filterName === FilterName.advertising_location) {
                optionValue = optionValue.filter((x) => (x as string).indexOf('profile') > -1)
            }
            if (!isNationalDistance(filterName, optionValue) && !isLocationFilter(filterName)) {
                filters.push({
                    filter: filterName,
                    selected: optionValue.map((value) => String(value)),
                })
            }
        }
    })

    return filters
}

export const updateFiltersFromUrl = (
    location: Location,
    facets: DeepWriteable<SearchResultsFacetsWithGroupsQuery_searchResults_SearchResults_facets_Facet[]>
) => {
    const filtersFromUrl = makeFiltersFromUrl(location)
    filtersFromUrl.forEach(({ filter, selected }) =>
        setFilterSelectedOptions(facets, filter, selected as OptionValue[])
    )
    const { pathname, search } = location
    setItemLocalStorage(SEARCH_URL_STORAGE_KEY, pathname + search)
}
